// Handle loading spritesheets, managing cursors, CSS

{
  Rectangle
  Texture
} from "pixi.js"

{ floor } := Math

Jadelet from jadelet
Observable, { type ObservableValue } from @danielx/observable

{ Modal } from @danielx/dui

{ SPRITE_SIZE, DEFAULT_SPRITE_SHA } from ../const.civet

SHALoader, { type SpritesheetChoices } from ./sha-loader.civet
{ PNGUtil, withoutExtension } from @danielx/lib

MidiSong from ./midi/song.civet

SpriteSheetConverterTemplate := Jadelet.exec """
  form.spritesheet-convert(@submit)
    h2 Import Spritesheet

    label
      h3 Preview
      .preview
        img(src=@previewSrc width=@previewWidth height=@previewHeight)

    label
      h3 Name
      input(value=@title name="title")

    label
      h3 Tile Width
      input(value=@tileWidth name="tileWidth")

    label
      h3 Tile Height
      input(value=@tileHeight name="tileHeight")

    label
      h3 Scale
      input(value=@scale name="scale")

    label
      h3 Includes Active State
      input(type="checkbox" checked=@includesActive name="includesActive")

    actions
      button Import
      button(click=@cancel) Cancel
"""

defaultMetadata :=
  includesActive: true
  scale: 1
  tileWidth: 48
  tileHeight: 48
  title: "default"

// The jsOn chunk comes from the import form, so its numbers arrive as STRINGS ("48"), and
// nothing downstream coerced them. That survived only because every sheet so far has a
// single row of tiles: the active frame's offset is `y * rowHeight + tileHeight`, and at
// y = 0 that is `0 + "48"` — which JS makes the string "048" and PIXI reads back as 48.
// On row 1 the same expression is "142" + "71" = "14271" and the texture lands 14000px
// down a 2414px sheet. Coerce once, here, rather than at each use.
normalizeMetadata := (m: Record<string, any>) ->
  {
    ...m
    tileWidth: Number(m.tileWidth) or defaultMetadata.tileWidth
    tileHeight: Number(m.tileHeight) or defaultMetadata.tileHeight
    scale: Number(m.scale) or 1
    includesActive: !!m.includesActive
  }

export Spritesheet := <Base extends Constructor<{}>>(S: Base) ->
  abstract class Spritesheet extends S
    abstract activeChannel: ObservableValue<number>
    abstract activeToolIndex: ObservableValue<number>
    abstract activeTool: ObservableValue<{ cursor: string }>
    abstract song: MidiSong
    abstract on(event: string, callback: Function): this

    spritesheetBlob: Blob?
    spritesheetImage: HTMLImageElement?
    spritesheetMetadata = { ...defaultMetadata }
    choicesLoaded: Promise<unknown>
    // The sha of the spritesheet whose textures are currently live. Sprite loading is
    // async and fires off a `song.spriteSha` observer, so this is the only way to tell
    // "the song's own sprites are up" from "the default ones still are" — which an
    // unattended render (app/render-cli.js) must wait for or it films blank notes.
    loadedSpriteSha: string?

    constructor(...args: any[])
      super(...args)

      resolveChoicesLoaded .= null
      @choicesLoaded .= new Promise (r) =>
        resolveChoicesLoaded = r

        @loadSpritesheetFromSha DEFAULT_SPRITE_SHA

      SHALoader.readSpritesheetChoicesCache()
      .then (choices) =>
        @spritesheetChoices choices
      .catch =>
        @spritesheetChoices [{
          name: "Default"
          sha: DEFAULT_SPRITE_SHA
        }]
      .then resolveChoicesLoaded

    initSpritesheet = =>
      @activeChannel.observe =>
        @setCursor()

      @activeToolIndex.observe =>
        @setCursor()

    spriteTextures = Observable [] as SpriteTexture[]
    spriteScale = =>
      @spritesheetMetadata.scale

    loadSpritesheetFromURL = (url: string, options?: LoadOptions) =>
      r := await fetch(url)

      if r.ok
        blob := await r.blob()
        return @loadSpritesheetFromBlob blob, options
      else
        throw new Error `Error loading url: ${url}`

    // Loads are async and unserialized: the default sheet kicks off at construction and
    // the song's own sheet off a `song.spriteSha` observer, and either can still be in
    // flight when a later load (an app/render-cli.js --spritesheet override) starts. Every
    // stage below re-checks this counter so a stale load can neither overwrite the
    // metadata nor land its textures on top of the newest one. Measured 2026-09-09: one of
    // nineteen unattended renders drew the stock 48px sheet through a 71px Touhou map.
    spriteLoadGeneration = 0

    loadSpritesheetFromBlob = (blob: Blob & { name?: string? }, options: LoadOptions={}) =>
      { nonInteractive } := options
      generation := ++@spriteLoadGeneration
      stale := => generation !== @spriteLoadGeneration
      @spritesheetBlob = blob

      chunks := await PNGUtil.parse blob
      return if stale()
      @spritesheetMetadata = normalizeMetadata PNGUtil.getJSON(chunks) or { ...defaultMetadata }

      Promise.resolve()
      .then =>
        if !nonInteractive
          // Update song meta data so we can link back to synth

          @registerSpritesheet blob
          .then (sha) =>
            @song.spriteSha sha
          .then ->
            return blob
        else
          return blob

      .then (blob) ->
        imageFromBlob blob
      .then (img) =>
        return if stale()
        if @spritesheetImage
          URL.revokeObjectURL @spritesheetImage.src

        @spritesheetImage = img
        @applySampleCSS img, @spritesheetMetadata

        loadSpritesheetFromImage img, @spritesheetMetadata
      .then (ts) =>
        return if !ts or stale()
        // revoke any old cursor urls
        @spriteTextures().forEach ({cursorURL}) ->
          URL.revokeObjectURL(cursorURL)

        @spriteTextures ts
        @setCursor()
        ts

    loadSpritesheetFromSha = (sha: string, options?: LoadOptions) =>
      SHALoader.bySha(sha)
      .then (buffer) ->
        new Blob [buffer], type: "image/png"
      .then (blob) =>
        @loadSpritesheetFromBlob blob, options
      .then (result) =>
        // A load superseded by a newer one resolves undefined and never went live, so it
        // must not claim to be the sheet whose textures are up.
        @loadedSpriteSha = sha if result
        result

    // Create a style element containing the background images for notes from a
    // list of samples. Applies background image when the class of the note is
    // the sample name.
    applySampleCSS = (image: HTMLImageElement, {includesActive, scale, tileWidth, tileHeight}: ImageOptions) =>
      style .= document.querySelector "style.samples"
      style ?= document.createElement "style"
      style.classList.add "samples"

      // TODO: scale?

      let rowHeight: number
      {width, height, src} := image
      if includesActive
        rowHeight = 2 * tileHeight
      else
        rowHeight = tileHeight

      tilesWide := floor width / tileWidth
      rowsTall := floor height / rowHeight

      paddingX := floor (tileWidth - SPRITE_SIZE) / 2
      paddingY := floor (tileHeight - SPRITE_SIZE) / 2

      style.innerHTML = [0...(tilesWide * rowsTall)].map (i) ->
        x := i % tilesWide
        y := floor i / tilesWide

        px := -x * tileWidth - paddingX
        py := -y * rowHeight - paddingY

        css .= ```
          tool.i${i}:after {background: url(${src}) ${px}px ${py}px;}
        ```

        if scale and scale != 1
          css += `tool.i${i}:after {transform: scale(${scale});}`

        if includesActive
          css += ```
            tool.i${i}.active:after {background: url(${src}) ${px}px ${py-tileHeight}px;}
          ```

        return css
      .join("\n")

      document.head.appendChild(style)

    setCursor = =>
      i := @activeToolIndex()
      if i is 0 // channel tool
        c := @activeChannel()
        p := @song.spriteForChannel(c) ?? 0

        textures := @spriteTextures()
        tex := textures[p % textures.length]

        if tex
          document.body.style.cursor = tex.cursor
      else // Other Tool
        cursor := @activeTool().cursor
        document.body.style.cursor = cursor

    spritesheetChoices = Observable [] as SpritesheetChoices
    registerSpritesheet = (blob: Blob & { name?: string? }) =>

      await @choicesLoaded
      chunks := await PNGUtil.parse blob

      blobName .= blob.name

      if blobName
        blobName = withoutExtension(blobName)

      metadata := PNGUtil.getJSON(chunks) or {}

      // Persist blob to local sha cache
      sha := await SHALoader.cache(blob)

      entry :=
        name: metadata.title
        sha: sha

      // TODO: check for existing and prompt for replacement
      existingChoices := @spritesheetChoices()
      added .= false

      choices := existingChoices.filter ({name}) ->
        name
      .map (choice) ->
        if entry.name is choice.name
          added = true
          entry
        else
          choice

      unless added
        choices.push entry

      choices.sort (a, b) ->
        a.name.localeCompare(b.name)

      SHALoader.cacheSpritesheetChoices choices
      @spritesheetChoices choices

      return sha

  return Spritesheet

// Add metadata to PNG
convert := (blob: Blob) ->
  chunks := await PNGUtil.parse blob

  blobName .= (blob as Blob & { name?: string }).name

  if blobName
    blobName = withoutExtension(blobName)

  metadata := PNGUtil.getJSON(chunks) or {}

  // The dialog shows the sheet's top-left corner at 2x so the tile size can be checked
  // against the art before committing. The object url lives until the form closes.
  preview := await imageFromBlob blob

  // auto scale?

  //@ts-ignore
  Modal.form SpriteSheetConverterTemplate
    previewSrc: preview.src
    previewWidth: preview.width * 2
    previewHeight: preview.height * 2
    scale: parseInt(metadata.scale, 10) or 1
    title: metadata.title or blobName or "Untitled"
    tileWidth: parseInt(metadata.tileWidth, 10) or 16
    tileHeight: parseInt(metadata.tileHeight, 10) or 16
    cancel: (e: Event) ->
      e.preventDefault()
      Modal.hide()
  .finally ->
    URL.revokeObjectURL preview.src
  .then (options) ->
    if !options
      throw "Cancelled"

    PNGUtil.blobWithJSON chunks, options

// An img from blob that doesn't revoke the url so it can be reused
// but you must remember to revoke the url when done with it
imageFromBlob := (blob: Blob) ->
  url := URL.createObjectURL(blob)

  new Promise<HTMLImageElement> (r, e) ->
    img := new Image

    img.onload = ->
      r(img)
    img.onerror = e

    img.src = url

// copy image data into a canvas to back PIXIJS textures
updateCanvas := (canvas: HTMLCanvasElement, image: HTMLImageElement) ->
  {width, height} := image // pixel width and height
  canvas.width = width
  canvas.height = height

  ctx := canvas.getContext('2d')!
  ctx.drawImage(image, 0, 0)
  return

// Image is in rows 96px tall
// not necessarily 16 wide
// |S1 |S2 |...|S16|
// |A1 |A2 |...|A16|
// |S17|S18|...
// |A17|A18|...

export type SpriteTexture = {
  normal: Texture
  active: Texture
  cursor: string
  cursorURL: string
}

type ImageOptions =
  includesActive?: boolean
  tileWidth: number
  tileHeight: number
  scale: number

// Creates PIXI Textures for the default and active states of each sprite.
loadSpritesheetFromImage := (img: HTMLImageElement, {includesActive, tileWidth, tileHeight, scale}: ImageOptions) ->
  // Canvas for the PIXIJS base texture
  canvas := document.createElement 'canvas'
  updateCanvas(canvas, img)

  {width, height} := img

  let rowHeight: number
  if includesActive
    rowHeight = 2 * tileHeight
  else
    rowHeight = tileHeight

  tilesWide := floor width / tileWidth
  tilesTall := floor height / rowHeight

  baseTexture := Texture.from(canvas)
  n := tilesWide * tilesTall

  Promise.all [0...n].map (i) ->
    x := i % tilesWide
    y := floor i / tilesWide

    //@ts-expect-error TODO: Update pixi.js
    normalTexture := new Texture baseTexture,
      new Rectangle(x * tileWidth, y * rowHeight, tileWidth, tileHeight)

    let activeTexture: Texture
    if includesActive
      //@ts-expect-error TODO: Update pixi.js
      activeTexture = new Texture baseTexture,
        new Rectangle(x * tileWidth, y * rowHeight + tileHeight, tileWidth, tileHeight)
    else
      activeTexture = normalTexture

    { cursor, url } := await makeCursor(normalTexture, scale)

    normal: normalTexture
    active: activeTexture
    cursor: cursor
    cursorURL: url

makeCursor := (texture: Texture, scale=1) ->
  {frame, baseTexture} := texture
  //@ts-expect-error TODO: Update pixi.js
  {source} := baseTexture.resource
  {x, y, width, height} := frame

  canvas := document.createElement 'canvas'
  canvas.width = width * scale
  canvas.height = height * scale
  ctx := canvas.getContext('2d')!
  ctx.imageSmoothingEnabled = false

  ctx.drawImage(source,
    x, y, width, height
    0, 0, canvas.width, canvas.height
  )

  new Promise<{cursor: string, url: string}> (resolve, reject) ->
    // Get blob url for each sprite cursor
    canvas.toBlob (blob) ->
      return reject() unless blob
      url := URL.createObjectURL blob

      resolve
        cursor: `url(${url}) ${canvas.width / 2} ${canvas.height / 2}, default`
        // url so we can revoke later
        url: url

Spritesheet.convert = convert

export default Spritesheet

type LoadOptions = { nonInteractive?: boolean? }
